import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import {
  listChecklist,
  createChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  reorderChecklist,
  type ChecklistItem,
  type ChecklistInput,
} from '@/api/checklist';

export function Checklist() {
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['checklist'],
    queryFn: listChecklist,
  });

  // Local working order — lets us drag/reorder without a round-trip per move.
  const [order, setOrder] = useState<ChecklistItem[]>([]);
  useEffect(() => setOrder(items), [items]);

  const [newText, setNewText] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const publicUrl = `${window.location.origin}/checklist/public`;
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: createChecklistItem,
    onSuccess: () => {
      setNewText('');
      setNewDescription('');
      qc.invalidateQueries({ queryKey: ['checklist'] });
    },
  });

  const update = useMutation({
    mutationFn: (vars: { id: string; input: ChecklistInput }) =>
      updateChecklistItem(vars.id, vars.input),
    onSuccess: () => {
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ['checklist'] });
    },
  });

  const remove = useMutation({
    mutationFn: deleteChecklistItem,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checklist'] }),
  });

  const reorder = useMutation({
    mutationFn: reorderChecklist,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checklist'] }),
  });

  function move(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= order.length) return;
    const next = order.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrder(next);
    reorder.mutate(next.map((it) => it.id));
  }

  function startEdit(it: ChecklistItem) {
    setEditingId(it.id);
    setEditText(it.text);
    setEditDescription(it.description ?? '');
  }

  function copyLink() {
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="max-w-3xl mx-auto py-6 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-content">Pre-publish Checklist</h1>
        <p className="mt-1 text-sm text-content-muted">
          Items shown on the public, no-login page that you can share with devs. Their tick marks aren't stored anywhere.
        </p>
      </div>

      <div className="bg-surface border border-border rounded-lg p-4">
        <div className="text-xs uppercase tracking-wide text-content-subtle font-semibold mb-2">
          Public share link
        </div>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={publicUrl}
            className="flex-1 px-3 py-2 text-sm bg-surface-muted border border-border-strong rounded-md font-mono text-content-muted"
            onFocus={(e) => e.currentTarget.select()}
          />
          <Button variant="secondary" size="md" onClick={copyLink}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center font-medium rounded-md transition-colors px-4 py-2 text-sm bg-surface text-content-muted border border-border-strong hover:bg-surface-muted"
          >
            Open
          </a>
        </div>
      </div>

      <EditOnly>
        <form
          className="bg-surface border border-border rounded-lg p-4 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            const t = newText.trim();
            if (!t) return;
            const d = newDescription.trim();
            create.mutate({ text: t, description: d ? d : null });
          }}
        >
          <input
            type="text"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Title — short, e.g. 'Cookies enabled in webview'"
            className="w-full px-3 py-2 text-sm border border-border-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Description (optional) — extra context shown under the title on the public page"
            rows={2}
            className="w-full px-3 py-2 text-sm border border-border-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-y"
          />
          <div className="flex justify-end">
            <Button type="submit" loading={create.isPending} disabled={!newText.trim()}>
              Add
            </Button>
          </div>
        </form>
      </EditOnly>

      <div className="bg-surface border border-border rounded-lg divide-y divide-border">
        {isLoading && (
          <div className="px-4 py-6 text-sm text-content-subtle">Loading…</div>
        )}
        {!isLoading && order.length === 0 && (
          <div className="px-4 py-6 text-sm text-content-subtle">No items yet.</div>
        )}
        {order.map((it, idx) => (
          <div key={it.id} className="flex items-start gap-2 px-3 py-2">
            <div className="flex flex-col text-content-subtle pt-1">
              <button
                type="button"
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                className="hover:text-content-muted disabled:opacity-30"
                aria-label="Move up"
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => move(idx, 1)}
                disabled={idx === order.length - 1}
                className="hover:text-content-muted disabled:opacity-30"
                aria-label="Move down"
              >
                ▼
              </button>
            </div>

            {editingId === it.id ? (
              <div className="flex-1 space-y-2">
                <input
                  type="text"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-border-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  autoFocus
                />
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Description (optional)"
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-border-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-y"
                />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      const t = editText.trim();
                      if (!t) return;
                      const d = editDescription.trim();
                      update.mutate({ id: it.id, input: { text: t, description: d ? d : null } });
                    }}
                    loading={update.isPending}
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-content">{it.text}</div>
                  {it.description && (
                    <div className="mt-0.5 text-xs text-content-subtle whitespace-pre-wrap break-words">
                      {it.description}
                    </div>
                  )}
                </div>
                <EditOnly>
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => startEdit(it)}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => {
                        if (confirm('Delete this item?')) remove.mutate(it.id);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </EditOnly>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
